import React, { FormEvent, useEffect, useRef, useState } from "react";
import {
  CameraIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { ShippingReportTemperatureReading } from "../../../types/shippingReports";

type ReadingInput = {
  temperatureCelsius: string;
  image: File;
  note?: string;
};

type Props = {
  readings: ShippingReportTemperatureReading[];
  disabled?: boolean;
  saving?: boolean;
  onAdd: (values: ReadingInput) => Promise<boolean>;
  onDelete: (reading: ShippingReportTemperatureReading) => void;
};

const inputClass =
  "mt-1 block min-w-0 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500";

const formatDate = (value: string) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Toronto",
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));

export default function ArrivalTemperatureReadings({
  readings,
  disabled,
  saving,
  onAdd,
  onDelete,
}: Props) {
  const cameraInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [adding, setAdding] = useState(false);
  const [temperature, setTemperature] = useState("");
  const [note, setNote] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!image) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(image);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);

  const reset = () => {
    setAdding(false);
    setTemperature("");
    setNote("");
    setImage(null);
    setError(null);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!temperature || !image) {
      setError("Temperature and a picture are required.");
      return;
    }
    const saved = await onAdd({
      temperatureCelsius: temperature,
      image,
      note,
    });
    if (saved) reset();
  };

  const acceptPicture = (files: FileList | null) => {
    if (files?.[0]) {
      setImage(files[0]);
      setError(null);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
        <div>
          <h2 className="text-base font-semibold text-slate-900">
            Arrival Temperature Readings
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Record as many timestamped readings as needed. Every reading requires a picture.
          </p>
        </div>
        {!adding ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              setAdding(true);
            }}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
          >
            <PlusIcon className="h-5 w-5" /> Add Reading
          </button>
        ) : null}
      </div>

      {adding ? (
        <form onSubmit={submit} className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/50 p-4">
          <div className="flex items-start justify-between">
            <h3 className="font-semibold text-slate-900">New arrival reading</h3>
            <button type="button" disabled={saving} onClick={reset} aria-label="Cancel reading" className="rounded p-1 text-slate-500 hover:bg-white">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-3 grid min-w-0 gap-4">
            <label className="min-w-0 text-sm font-medium text-slate-700">
              Temperature (°C)
              <input type="number" step="0.01" min="-100" max="100" value={temperature} onChange={(event) => setTemperature(event.target.value)} className={inputClass} />
            </label>
            <p className="text-xs text-slate-500">The reading time is recorded automatically when the picture is saved.</p>
            <label className="min-w-0 text-sm font-medium text-slate-700">
              Optional Note
              <input type="text" maxLength={255} value={note} onChange={(event) => setNote(event.target.value)} className={inputClass} />
            </label>
          </div>

          <input ref={cameraInput} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="hidden" onChange={(event) => { acceptPicture(event.target.files); event.target.value = ""; }} />
          <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(event) => { acceptPicture(event.target.files); event.target.value = ""; }} />

          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" onClick={() => cameraInput.current?.click()} className="inline-flex items-center gap-2 rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm font-semibold text-indigo-800">
              <CameraIcon className="h-5 w-5" /> Take Picture
            </button>
            <button type="button" onClick={() => fileInput.current?.click()} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700">
              Choose Picture
            </button>
          </div>

          {preview ? (
            <div className="mt-3 overflow-hidden rounded-lg border border-slate-200 bg-white p-2">
              <img src={preview} alt="Selected temperature evidence" className="max-h-64 w-full object-contain" />
              <p className="mt-1 truncate text-xs text-slate-500">{image?.name}</p>
            </div>
          ) : null}
          {error ? <p className="mt-3 text-sm font-medium text-rose-700">{error}</p> : null}

          <div className="mt-4 flex justify-end gap-2">
            <button type="button" disabled={saving} onClick={reset} className="rounded-md border border-slate-300 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700">Cancel</button>
            <button type="submit" disabled={saving || disabled} className="rounded-md bg-main-purple px-3.5 py-2 text-sm font-semibold text-white disabled:opacity-50">
              {saving ? "Saving Reading..." : "Save Reading"}
            </button>
          </div>
        </form>
      ) : null}

      {readings.length === 0 ? (
        <div className="mt-4 rounded-lg border-2 border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">
          No arrival temperature readings recorded yet.
        </div>
      ) : (
        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {[...readings]
            .sort((a, b) => a.recorded_at.localeCompare(b.recorded_at))
            .map((reading) => (
              <article key={reading.id} className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                <div className="flex h-48 items-center justify-center bg-slate-100">
                  <img src={reading.image_url} alt={`Temperature evidence at ${reading.temperature_celsius} degrees Celsius`} className="h-full w-full object-contain" />
                </div>
                <div className="p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-lg font-bold text-slate-900">{reading.temperature_celsius} °C</p>
                      <p className="text-xs text-slate-500">{formatDate(reading.recorded_at)}</p>
                    </div>
                    <button type="button" disabled={disabled} onClick={() => { if (window.confirm("Delete this temperature reading and its picture?")) onDelete(reading); }} className="rounded p-1.5 text-rose-600 hover:bg-rose-50 disabled:opacity-30" aria-label="Delete temperature reading">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                  {reading.note ? <p className="mt-2 text-sm text-slate-600">{reading.note}</p> : null}
                </div>
              </article>
            ))}
        </div>
      )}
    </section>
  );
}
